import React, { useEffect, useState, useCallback } from "react";
import * as S from "./AdminBuildings.styled";
import apiClient from "services/api";
import { adminBuildingService } from "services/adminBuildingService";
import { Building } from "types";
import AdminBuildingDrawer from "./AdminBuildingDrawer";
import AdminBuildingDetail from "./AdminBuildingDetail";

const AdminBuildings: React.FC = () => {
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const fetchBuildings = useCallback(() => {
    const universityId = localStorage.getItem("adminUniversityId");
    setIsLoading(true);
    apiClient
      .get<Building[]>(`/universities/${universityId}/buildings`)
      .then((res) => setBuildings(res.data))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchBuildings();
  }, [fetchBuildings]);

  const handleSuccess = () => {
    setIsDrawerOpen(false);
    fetchBuildings();
  };

  const handleRowClick = (id: number) => {
    setSelectedBuildingId(id);
  };

  const handleDelete = async (e: React.MouseEvent, buildingId: number) => {
    e.stopPropagation();
    if (!window.confirm("건물을 삭제하시겠습니까?")) return;
    setDeletingId(buildingId);
    try {
      await adminBuildingService.deleteBuilding(buildingId);
      fetchBuildings();
    } catch {
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = buildings.filter(
    (b) =>
      b.name.toLowerCase().includes(query.toLowerCase()) ||
      b.label.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <>
      <S.PageHeader>
        <S.PageTitle>전체 건물 목록</S.PageTitle>
        <S.AddButton onClick={() => setIsDrawerOpen(true)}>+ 건물 추가</S.AddButton>
      </S.PageHeader>

      <S.Toolbar>
        <S.SearchWrap>
          <S.SearchIcon>🔍</S.SearchIcon>
          <S.SearchInput
            placeholder="건물명 또는 약칭으로 검색..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </S.SearchWrap>
        <S.CountText>
          {isLoading ? "로딩 중..." : `총 ${filtered.length}개 건물`}
        </S.CountText>
      </S.Toolbar>

      <S.Card>
        <S.Table>
          <S.Thead>
            <tr>
              <S.Th width="56px">#</S.Th>
              <S.Th width="72px">썸네일</S.Th>
              <S.Th>건물명</S.Th>
              <S.Th width="200px">좌표</S.Th>
              <S.Th width="80px"></S.Th>
            </tr>
          </S.Thead>
          <S.Tbody>
            {!isLoading && filtered.length === 0 ? (
              <tr>
                <S.EmptyCell colSpan={5}>
                  {query
                    ? `"${query}"에 해당하는 건물이 없습니다.`
                    : "등록된 건물이 없습니다."}
                </S.EmptyCell>
              </tr>
            ) : (
              filtered.map((building, index) => (
                <S.Tr key={building.id} onClick={() => handleRowClick(building.id)} style={{ cursor: "pointer" }}>
                  <S.Td>
                    <S.IndexCell>{index + 1}</S.IndexCell>
                  </S.Td>
                  <S.Td>
                    {building.thumbnailImage ? (
                      <S.Thumbnail
                        src={building.thumbnailImage}
                        alt={building.name}
                      />
                    ) : (
                      <S.ThumbnailPlaceholder>🏢</S.ThumbnailPlaceholder>
                    )}
                  </S.Td>
                  <S.Td>
                    <S.BuildingName>{building.name}</S.BuildingName>
                    <S.BuildingLabel>{building.label}</S.BuildingLabel>
                  </S.Td>
                  <S.Td>
                    <S.CoordText>
                      {building.latitude.toFixed(6)},{" "}
                      {building.longitude.toFixed(6)}
                    </S.CoordText>
                  </S.Td>
                  <S.Td>
                    <S.DeleteButton
                      disabled={deletingId === building.id}
                      onClick={(e) => handleDelete(e, building.id)}
                    >
                      {deletingId === building.id ? "삭제 중..." : "삭제"}
                    </S.DeleteButton>
                  </S.Td>
                </S.Tr>
              ))
            )}
          </S.Tbody>
        </S.Table>
      </S.Card>

      {isDrawerOpen && (
        <AdminBuildingDrawer
          onClose={() => setIsDrawerOpen(false)}
          onSuccess={handleSuccess}
        />
      )}

      {selectedBuildingId !== null && (
        <AdminBuildingDetail
          buildingId={selectedBuildingId}
          onClose={() => setSelectedBuildingId(null)}
        />
      )}
    </>
  );
};

export default AdminBuildings;
